import { useRef } from "react";
import {
  ArrowRight,
  FileUp,
  FolderOpen,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

type Props = {
  isAuthenticated: boolean;
  onBack: () => void;
  onPickLocalFile: (files: FileList | null) => void;
  onOpenCloudDocuments: () => void;
  onOpenEsignSetup: () => void;
};

export function SignStartPage({
  isAuthenticated,
  onBack,
  onPickLocalFile,
  onOpenCloudDocuments,
  onOpenEsignSetup,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flow-page consumer-shell">
      <header className="flow-page-head">
        <button type="button" className="text-back" onClick={onBack}>
          <ArrowRight size={16} />
          بازگشت
        </button>
        <span className="consumer-kicker">امضای اسناد</span>
        <h1>فایل را انتخاب کنید، سپس نوع جریان امضا را تعیین کنید</h1>
        <p>
          بعد از باز شدن فایل، همان sheet امضای فعلی نویساین باز می‌شود تا بین
          «فقط خودم امضا کنم» و «دیگران را هم اضافه کنم» انتخاب کنید.
        </p>
        <div className="consumer-chip-row" aria-label="مسیرهای امضا">
          <span className="consumer-chip">
            <ShieldCheck size={14} />
            امضای خودم
          </span>
          <span className="consumer-chip">
            <Users size={14} />
            ارسال برای دیگران
          </span>
          <span className="consumer-chip">
            <Sparkles size={14} />
            reuse همان flow فعلی
          </span>
        </div>
      </header>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={(e) => {
          onPickLocalFile(e.target.files);
          e.target.value = "";
        }}
      />

      <button
        type="button"
        className="flow-option-card sign-start-card accent-soft"
        onClick={() => inputRef.current?.click()}
      >
        <FileUp size={22} className="flow-option-icon" aria-hidden />
        <div className="flow-option-copy">
          <strong>انتخاب فایل PDF یا Word</strong>
          <span>فایل محلی را باز کنید تا بلافاصله وارد sheet انتخاب مسیر امضا شوید.</span>
        </div>
      </button>

      <button
        type="button"
        className={`flow-option-card sign-start-card${isAuthenticated ? "" : " locked"}`}
        onClick={onOpenCloudDocuments}
      >
        <FolderOpen size={22} className="flow-option-icon" aria-hidden />
        <div className="flow-option-copy">
          <strong>
            انتخاب از اسناد نویساین
            {!isAuthenticated ? " · نیاز به ورود" : ""}
          </strong>
          <span>
            اگر سند در حساب شما ثبت شده است، آن را از فهرست اسناد باز کنید و همان‌جا
            وارد جریان امضا شوید.
          </span>
        </div>
      </button>

      <section
        className="dashboard-section sign-start-note sign-start-profile-cta"
        aria-labelledby="sign-start-profile-title"
      >
        <div className="sign-start-profile-head">
          <span className="sign-start-profile-icon" aria-hidden>
            <ShieldCheck size={20} />
          </span>
          <div className="sign-start-profile-copy">
            <span className="sign-start-profile-kicker">پیش‌نیاز امضای مطمئن</span>
            <h2 id="sign-start-profile-title">آماده‌سازی پروفایل امضا</h2>
            <p>
              اگر می‌خواهید امضای مطمئن درون‌سامانه یا گواهی‌محور فعال شود، مسیر
              احراز هویت و گواهی را هم از اینجا در دسترس دارید.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="primary-btn sign-start-profile-btn"
          onClick={onOpenEsignSetup}
        >
          <ShieldCheck size={16} aria-hidden />
          احراز هویت و تنظیم گواهی امضا
        </button>
        <p className="settings-hint">
          پشتیبانی مستقیم از فایل تصویر در ورودی این مسیر هنوز کامل نیست؛ برای
          تصاویر فعلاً از اسکن یا تبدیل به PDF استفاده کنید.
        </p>
      </section>
    </div>
  );
}
